import type {
    MeetingJoinFailure,
    MeetingRefusalReason,
} from "../../domain/meetingRefusal";

/**
 * The API's codes for a room that will not open.
 *
 * A Map rather than an object, so a `detail` that happens to name something
 * every object inherits — `constructor`, say — is not read as a reason.
 */
const REASON_BY_CODE = new Map<string, MeetingRefusalReason>([
    ["meeting_not_open", "notOpenYet"],
    ["meeting_ended", "ended"],
    ["meeting_cancelled", "cancelled"],
    ["meeting_not_hosted", "notHostedHere"],
]);

type RefusalBody = {
    detail?: unknown;
    opens_at?: unknown;
    closes_at?: unknown;
};

const instant = (value: unknown): Date | null => {
    if (typeof value !== "string") return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * A failed request for a meeting token, read into what it means.
 *
 * Both doors into a room answer a refusal to open the same way — 409, the
 * reason as `detail`, and the window beside it when there is one — so it is
 * read here, once, and neither page parses an HTTP error of its own.
 */
export function meetingJoinFailureFrom(error: unknown): MeetingJoinFailure {
    const response = (
        error as { response?: { status?: number; data?: RefusalBody } }
    )?.response;

    switch (response?.status) {
        case 409: {
            const code = response.data?.detail;
            return {
                kind: "refused",
                refusal: {
                    reason:
                        (typeof code === "string" && REASON_BY_CODE.get(code)) ||
                        "unrecognised",
                    opensAt: instant(response.data?.opens_at),
                    closesAt: instant(response.data?.closes_at),
                },
            };
        }
        case 403:
            return { kind: "forbidden" };
        case 404:
            return { kind: "notFound" };
        default:
            return { kind: "unavailable" };
    }
}
